// Copyright ©️ 2026 eVolve MEP, LLC

import { useState } from 'react';

import { ActionIcon, NumberInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { type NodeProps, useReactFlow } from '@xyflow/react';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { AgGridStyleTooltip } from 'components/Mantine/AgGridStyleTooltip';
import { FormPopover, useFormPopover } from 'components/Mantine/Popovers/FormPopover';
import { isNotNil } from 'helpers/isNotNil';
import type { PartNodeType } from 'modules/Shop/CatalogSetup/PartEditor/nodes/types';
import { useAssemblyEditor } from 'modules/Shop/CatalogSetup/PartEditor/useAssemblyEditor';

const EditNodeFields = ({ node: { data, id } }: { node: NodeProps<PartNodeType> }) => {
  const { getNode } = useReactFlow();
  const { onNodesChange } = useAssemblyEditor();
  const form = useForm<Pick<typeof data, 'quantity'>>({
    initialValues: data,
    validate: {
      quantity: (v) => !Number.isInteger(v),
    },
  });

  const onSubmit = async (values: typeof form.values) => {
    const node = getNode(id);
    if (isNotNil(node)) {
      onNodesChange([
        {
          id,
          type: 'replace',
          item: {
            ...node,
            data: {
              ...node.data,
              ...values,
            },
          },
        },
      ]);
    }
  };

  useFormPopover(form, onSubmit);

  return (
    <NumberInput
      label="Unit Qty"
      style={{ width: 100 }}
      max={999_999}
      clampBehavior="strict"
      min={1}
      startValue={1}
      autoFocus
      data-autofocus
      placeholder="Unit Qty"
      {...form.getInputProps('quantity')}
    />
  );
};

export const EditNode = ({ node }: { node: NodeProps<PartNodeType> }) => {
  const [editing, setEditing] = useState(false);

  return (
    <FormPopover
      offset={40}
      opened={editing}
      confirmationText="Save"
      onClose={() => setEditing(false)}
      target={
        <ActionIcon
          className={editing ? undefined : 'hover-title--on'}
          variant="light"
          style={{
            position: 'absolute',
            right: 6,
            top: 4,
          }}
          onClick={(e) => {
            setEditing(true);
            e.stopPropagation();
          }}
        >
          <AgGridStyleTooltip label="Edit part" withArrow>
            <EvolveIcon icon="Edit" />
          </AgGridStyleTooltip>
        </ActionIcon>
      }
    >
      <EditNodeFields node={node} />
    </FormPopover>
  );
};
